<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { API } from "@/api/api";
import { useDateTime } from "@/composables/useDateTime";
import { useReservationLogs } from "@/composables/reactive/useReservationLogs";
import { useNow } from "@/composables/reactive/useNow";
import { useTruck } from "@/composables/useTruck";
import { useAppStore } from "@/store/app.store";
import {
  RESERVATION_HISTORY_PANEL,
  TRUCK_RESERVATION_MINUTES,
  TRUCK_RESERVATIONS_DIALOG_PAGE_SIZE,
  TruckViewTab,
  TruckViewTabHash,
} from "@/constants/truck";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import TruckReservationsTable from "@/views/fleets/components/TruckReservationsTable.vue";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";

const props = withDefaults(
  defineProps<{
    truck: TruckDto;
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  },
);

const emit = defineEmits<{
  (e: "success", truck: TruckDto): void;
  (e: "error", error: unknown): void;
}>();

const api = new API();
const router = useRouter();
const { t } = useI18n();
const { dateTZ } = useDateTime();
const { getReservationTime, isReservedByDispatcher, getReservedBy } = useTruck();
const { now } = useNow();
const {
  reservationLogs,
  reservationLogsTotal,
  reservationLogsParams,
  reservationLogsLoading,
  changeReservationLogsPage,
  changeReservationLogsPageSize,
} = useReservationLogs(() => props.truck.id, TRUCK_RESERVATIONS_DIALOG_PAGE_SIZE);
const appStore = useAppStore();

const dialogVisible = ref<boolean>(false);
const saving = ref<boolean>(false);
const openedPanel = ref<string | null>(null);
const dispatcherId = ref<number | null>(null);
const time = ref<number>(20);

const reservationMinutes = computed<number>(() => getReservationTime(props.truck, now.value));

const reservationExpired = computed<boolean>(() => reservationMinutes.value < 0);

const reservedByMe = computed(() => {
  const currentDispatcherId = appStore.dispatcher?.id;

  return !!currentDispatcherId && isReservedByDispatcher(props.truck, currentDispatcherId) && !reservationExpired.value;
});

const reservedByOther = computed<boolean>(() => {
  return Boolean(props.truck.reservedById) && !reservationExpired.value && !reservedByMe.value;
});

const primaryActionLabel = computed<string | null>(() => {
  if (reservedByOther.value) {
    return null;
  }

  return reservedByMe.value ? "Save" : "Reserve";
});

const minuteOptions = computed(() => {
  return TRUCK_RESERVATION_MINUTES.map((minutes) => ({ id: minutes, name: `${minutes} minutes` }));
});

const otherDispatchers = computed(() => {
  return appStore.dispatchers.filter((dispatcher) => dispatcher.id !== appStore.dispatcher?.id);
});

const reservedTill = computed(() => {
  return appStore.dispatcher && props.truck.reservedTill
    ? dateTZ(props.truck.reservedTill, appStore.dispatcher.timezone)
    : "";
});

const title = computed(() => {
  if (!reservedByMe.value || !props.truck.reservedById || reservationExpired.value) {
    return `Reserve truck #${props.truck.number}`;
  }

  return `Truck #${props.truck.number} reservation management`;
});

watch(openedPanel, async (value) => {
  if (value === RESERVATION_HISTORY_PANEL) {
    await changeReservationLogsPage(1);
  }
});

watch(dialogVisible, (value) => {
  if (!value) {
    openedPanel.value = null;
  }
});

function showDialog() {
  if (!props.disabled) {
    dialogVisible.value = true;
  }
}

function closeDialog() {
  dialogVisible.value = false;
}

async function goToReservations() {
  if (!props.truck.id) {
    return;
  }

  closeDialog();

  await router.push({
    name: "truck-view",
    params: { id: props.truck.id },
    hash: `#${TruckViewTabHash[TruckViewTab.reservations]}`,
  });
}

async function save() {
  if (!props.truck.reservedById || reservedByMe.value || reservationExpired.value) {
    await reserveTruck(dispatcherId.value ?? appStore.dispatcher?.id ?? null, time.value);
  } else {
    closeDialog();
  }
}

async function releaseTruck() {
  if (reservedByMe.value) {
    await reserveTruck(null);
  } else {
    closeDialog();
  }
}

async function reserveTruck(reservedById: number | null, minutes: number | null = null) {
  if (!props.truck.id) {
    return;
  }

  saving.value = true;

  try {
    const resp = await api.trucks.reserveTruck(props.truck.id, reservedById, minutes);

    if (resp.success && resp.data) {
      dispatcherId.value = null;
      emit("success", resp.data);
    }
  } catch (error) {
    console.error("Error reserving truck: " + error);
    emit("error", error);
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div @click="showDialog">
    <slot></slot>
  </div>

  <omni-dialog
    v-model="dialogVisible"
    :primary-action-label="primaryActionLabel"
    :primary-button-loading="saving"
    :secondary-action-label="reservedByMe ? 'Release truck' : null"
    :secondary-button-disabled="saving"
    :title="title"
    :width="1000"
    @primary-action:click="save"
    @secondary-action:click="releaseTruck"
  >
    <span class="text-grey-darken-3">
      {{ truck.reservedById && !reservationExpired ? `Reserved by ${getReservedBy(truck)} until ${reservedTill}` : "" }}
    </span>
    <div class="my-5">
      <div v-if="truck.reservedById && !reservationExpired" class="mb-4"></div>

      <v-select
        v-if="reservedByMe"
        v-model="dispatcherId"
        :items="otherDispatchers"
        autocomplete="off"
        class="mb-7"
        color="primary"
        density="compact"
        hide-details
        item-title="name"
        item-value="id"
        label="Transfer reservation to"
        variant="outlined"
      />

      <v-select
        v-if="!reservedByOther"
        v-model="time"
        :items="minuteOptions"
        :label="reservedByMe ? 'Extend reservation for' : 'Reserve for'"
        autocomplete="off"
        color="primary"
        density="compact"
        hide-details
        item-title="name"
        item-value="id"
        variant="outlined"
      />
    </div>

    <v-expansion-panels v-model="openedPanel" class="reservation-history">
      <v-expansion-panel :value="RESERVATION_HISTORY_PANEL" elevation="0">
        <v-expansion-panel-title class="font-weight-500 text-grey-darken-3 px-0 justify-start" width="942">
          {{ t("trucks.reservation-history") }}
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <truck-reservations-table
            :items="reservationLogs"
            :loading="reservationLogsLoading"
            :page="reservationLogsParams.page"
            :page-size="reservationLogsParams.pageSize"
            :total="reservationLogsTotal"
            :truck-number="truck.number"
            @update:page="changeReservationLogsPage"
            @update:page-size="changeReservationLogsPageSize"
          />

          <v-btn class="font-weight-600" color="primary" variant="text" @click="goToReservations">
            <span>
              {{ t("trucks.view-all-reservations") }}
            </span>
          </v-btn>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <template v-if="reservedByOther" #actions:custom>
      <v-tooltip location="top">
        <template #activator="{ props: tooltipProps }">
          <span v-bind="tooltipProps">
            <v-btn
              class="text-uppercase font-weight-600 font-size-14 px-4"
              color="success"
              data-qa="reserve-truck-reserved"
              readonly
              variant="flat"
            >
              {{ t("trucks.reserved-minutes", { minutes: reservationMinutes }) }}
            </v-btn>
          </span>
        </template>

        {{ t("trucks.reserved-by-until", { dispatcher: getReservedBy(truck), date: reservedTill }) }}
      </v-tooltip>
    </template>
  </omni-dialog>
</template>

<style lang="scss" scoped>
.reservation-history {
  width: 100%;

  :deep(.v-expansion-panel-text__wrapper) {
    padding: 0;
  }

  :deep(.v-expansion-panel__shadow) {
    box-shadow: none;
  }

  :deep(.v-data-table-footer) {
    position: static;
    width: auto;
    box-shadow: none;
    padding: 0;
  }

  :deep(.v-data-table-footer__items-per-page) {
    display: none;
  }

  :deep(.omni-table) {
    margin-bottom: 0;
  }

  :deep(.v-expansion-panel-title__icon) {
    margin-left: 8px;
  }
}
</style>
